'use client';

import { c as _c } from "react/compiler-runtime";
import './header.scss';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { usePathname } from 'next/navigation';
import { Suspense, useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { LogoChooser } from '@sola/ark-core/atoms/logos/logos.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { LogoName } from '@sola/schema/lib/logos.ts';
import type { Link as LinkProps } from '@sola/schema/lib/schema.ts';
import type { HeaderProps } from '@sola/schema/lib/skippy.ts';
import { trackClick } from '@sola/tracking/lib/analytics/events.ts';
import { HeaderDesktopActions, HeaderMobileActions } from './header-actions.tsx';
import { DesktopMenuItem } from './header-desktop-item.tsx';
import { MobileMenu } from './header-mobile-menu.tsx';
import { useHeaderLogic } from './header.logic.ts';

/**
 * Watches for pathname changes and calls onNavigate.
 * Wrapped in Suspense so usePathname() doesn't block the static shell.
 */
const PathnameWatcher = t0 => {
  const $ = _c(5);
  const {
    onNavigate
  } = t0;
  const pathname = usePathname();
  let t1;
  if ($[0] !== onNavigate) {
    t1 = () => {
      onNavigate();
    };
    $[0] = onNavigate;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onNavigate || $[3] !== pathname) {
    t2 = [pathname, onNavigate];
    $[2] = onNavigate;
    $[3] = pathname;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useLayoutEffect(t1, t2);
  return null;
};
type CustomizeProps = {
  logo?: LogoName;
  link?: Omit<LinkProps, 'title'> & {
    title?: string;
  };
  showShoppingBag?: boolean;
  showLogin?: boolean;
  showSearch?: boolean;
  urlLogin?: string;
  urlAccountHome?: string;
  userMenu?: React.ReactNode;
  /**
   * Set by apps where middleware guarantees an authenticated user (my), so the
   * account area doesn't flash a login button while auth resolves.
   */
  assumeAuthenticated?: boolean;
};
export const Header: React.FC<HeaderProps & CustomizeProps> = t0 => {
  const $ = _c(118);
  const {
    primaryMenu,
    logo: t1,
    link,
    showShoppingBag,
    showLogin,
    showSearch,
    urlLogin,
    urlAccountHome,
    userMenu,
    assumeAuthenticated
  } = t0;
  const logo = t1 === undefined ? "compassion" : t1;
  let t2;
  if ($[0] !== primaryMenu.items) {
    t2 = primaryMenu.items || [];
    $[0] = primaryMenu.items;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const menuItems = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["priority"];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    t
  } = useTranslation(t3);
  let t4;
  if ($[3] !== link?.title || $[4] !== t) {
    t4 = link?.title || t("priority:header.logoAlt");
    $[3] = link?.title;
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const logoLabel = t4;
  let t5;
  if ($[6] !== menuItems) {
    t5 = {
      menuItems
    };
    $[6] = menuItems;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    menuOpen,
    navKey,
    navStack,
    setNavStack,
    isFullscreenMenuOpen,
    headerHidden,
    refs: t6,
    hoverHandlers: t7,
    callbacks: t8
  } = useHeaderLogic(t5);
  const {
    mobileCloseButtonRef,
    mobileOpenButtonRef,
    accountTriggerRef,
    hamburgerTriggerRef
  } = t6;
  const {
    handleTriggerEnter,
    handleTriggerLeave,
    handleContentEnter,
    handleContentLeave,
    handleViewportEnter,
    handleViewportLeave,
    setActiveItem
  } = t7;
  const {
    handlePathnameChange,
    handleMobileClose,
    handleMobileBack,
    handleAccountOpenFromHamburger,
    handleAccountClose,
    handleActionNavValueChange
  } = t8;
  let t9;
  if ($[8] !== headerHidden) {
    t9 = cn("Header", {
      "Header--hidden": headerHidden
    });
    $[8] = headerHidden;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== handlePathnameChange) {
    t10 = <Suspense><PathnameWatcher onNavigate={handlePathnameChange} /></Suspense>;
    $[10] = handlePathnameChange;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t) {
    t11 = t("header.skipLink");
    $[12] = t;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t11) {
    t12 = <ButtonStyle><a className="Header__skip-link" href="#main">{t11}</a></ButtonStyle>;
    $[14] = t11;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== isFullscreenMenuOpen) {
    t13 = cn("Header__container", {
      "is-mobile-open": isFullscreenMenuOpen
    });
    $[16] = isFullscreenMenuOpen;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const t14 = link?.link || "/";
  let t15;
  if ($[18] !== logo) {
    t15 = <LogoChooser name={logo} className="Header__logo" color="full" aria-labelledby="Header-logo-label" priority={true} />;
    $[18] = logo;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== logoLabel) {
    t16 = <span id="Header-logo-label" className="sr-only">{logoLabel}</span>;
    $[20] = logoLabel;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== logoLabel || $[23] !== t14 || $[24] !== t15 || $[25] !== t16) {
    t17 = <div className="Header__logo-container"><Link href={t14} title={logoLabel}>{t15}{t16}</Link></div>;
    $[22] = logoLabel;
    $[23] = t14;
    $[24] = t15;
    $[25] = t16;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  const t18 = `main-nav-${navKey}`;
  let t19;
  if ($[27] !== handleContentEnter || $[28] !== handleContentLeave || $[29] !== handleTriggerEnter || $[30] !== handleTriggerLeave || $[31] !== menuItems || $[32] !== menuOpen || $[33] !== setActiveItem) {
    let t20;
    if ($[35] !== handleContentEnter || $[36] !== handleContentLeave || $[37] !== handleTriggerEnter || $[38] !== handleTriggerLeave || $[39] !== menuOpen || $[40] !== setActiveItem) {
      t20 = item => <DesktopMenuItem key={item.title} item={item} menuOpen={menuOpen} handleTriggerEnter={handleTriggerEnter} handleTriggerLeave={handleTriggerLeave} handleContentEnter={handleContentEnter} handleContentLeave={handleContentLeave} setActiveItem={setActiveItem} />;
      $[35] = handleContentEnter;
      $[36] = handleContentLeave;
      $[37] = handleTriggerEnter;
      $[38] = handleTriggerLeave;
      $[39] = menuOpen;
      $[40] = setActiveItem;
      $[41] = t20;
    } else {
      t20 = $[41];
    }
    t19 = menuItems.map(t20);
    $[27] = handleContentEnter;
    $[28] = handleContentLeave;
    $[29] = handleTriggerEnter;
    $[30] = handleTriggerLeave;
    $[31] = menuItems;
    $[32] = menuOpen;
    $[33] = setActiveItem;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[42] !== t19) {
    t20 = <NavigationMenu.List className="Header__menu-list" asChild={true}><div><ul className="Header__desktop-items xl:mx-roomy-xs">{t19}</ul></div></NavigationMenu.List>;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <NavigationMenu.NavigationMenuIndicator />;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== handleViewportEnter || $[46] !== handleViewportLeave) {
    t22 = <div className="Header__viewport-position"><NavigationMenu.Viewport className="Header__menu-viewport flex justify-center" onPointerEnter={handleViewportEnter} onPointerLeave={handleViewportLeave} onFocus={handleViewportEnter} onBlur={handleViewportLeave} /></div>;
    $[45] = handleViewportEnter;
    $[46] = handleViewportLeave;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== menuOpen || $[49] !== t18 || $[50] !== t20 || $[51] !== t22) {
    t23 = <NavigationMenu.Root key={t18} aria-label="Main navigation" value={menuOpen} className="Header__root flex-col hidden lg:flex">{t20}{t21}{t22}</NavigationMenu.Root>;
    $[48] = menuOpen;
    $[49] = t18;
    $[50] = t20;
    $[51] = t22;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  const t24 = `action-nav-${navKey}`;
  let t25;
  if ($[53] !== accountTriggerRef || $[54] !== assumeAuthenticated || $[55] !== handleAccountClose || $[56] !== showLogin || $[57] !== showSearch || $[58] !== showShoppingBag || $[59] !== urlAccountHome || $[60] !== urlLogin || $[61] !== userMenu) {
    t25 = <HeaderDesktopActions showSearch={showSearch} showShoppingBag={showShoppingBag} showLogin={showLogin} urlLogin={urlLogin} urlAccountHome={urlAccountHome} userMenu={userMenu} assumeAuthenticated={assumeAuthenticated} accountTriggerRef={accountTriggerRef} onAccountClose={handleAccountClose} />;
    $[53] = accountTriggerRef;
    $[54] = assumeAuthenticated;
    $[55] = handleAccountClose;
    $[56] = showLogin;
    $[57] = showSearch;
    $[58] = showShoppingBag;
    $[59] = urlAccountHome;
    $[60] = urlLogin;
    $[61] = userMenu;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== t) {
    t26 = () => {
      trackClick(t("header.menu"));
    };
    $[63] = t;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  let t27;
  if ($[65] !== t) {
    t27 = t("header.menu");
    $[65] = t;
    $[66] = t27;
  } else {
    t27 = $[66];
  }
  let t28;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <ArkIcon iconName="menu-01" className="Header__nav-icon" />;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== hamburgerTriggerRef || $[69] !== t26 || $[70] !== t27) {
    t29 = <NavigationMenu.Trigger ref={hamburgerTriggerRef} onPointerEnter={_temp} onPointerMove={_temp2} onPointerLeave={_temp3} onClick={t26} className="flex items-center p-comfy-xs rounded-full" title={t27}>{t28}</NavigationMenu.Trigger>;
    $[68] = hamburgerTriggerRef;
    $[69] = t26;
    $[70] = t27;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  let t30;
  if ($[72] !== assumeAuthenticated || $[73] !== handleAccountOpenFromHamburger || $[74] !== showLogin || $[75] !== showSearch || $[76] !== showShoppingBag || $[77] !== urlAccountHome || $[78] !== urlLogin || $[79] !== userMenu) {
    t30 = <HeaderMobileActions showSearch={showSearch} showShoppingBag={showShoppingBag} showLogin={showLogin} urlLogin={urlLogin} urlAccountHome={urlAccountHome} userMenu={userMenu} assumeAuthenticated={assumeAuthenticated} onAccountOpen={handleAccountOpenFromHamburger} />;
    $[72] = assumeAuthenticated;
    $[73] = handleAccountOpenFromHamburger;
    $[74] = showLogin;
    $[75] = showSearch;
    $[76] = showShoppingBag;
    $[77] = urlAccountHome;
    $[78] = urlLogin;
    $[79] = userMenu;
    $[80] = t30;
  } else {
    t30 = $[80];
  }
  let t31;
  if ($[81] !== handleMobileBack || $[82] !== handleMobileClose || $[83] !== mobileCloseButtonRef || $[84] !== mobileOpenButtonRef || $[85] !== navStack || $[86] !== setNavStack || $[87] !== showLogin || $[88] !== t30) {
    t31 = <MobileMenu navStack={navStack} setNavStack={setNavStack} onClose={handleMobileClose} onBack={handleMobileBack} mobileCloseButtonRef={mobileCloseButtonRef} mobileOpenButtonRef={mobileOpenButtonRef} showLogin={showLogin}>{t30}</MobileMenu>;
    $[81] = handleMobileBack;
    $[82] = handleMobileClose;
    $[83] = mobileCloseButtonRef;
    $[84] = mobileOpenButtonRef;
    $[85] = navStack;
    $[86] = setNavStack;
    $[87] = showLogin;
    $[88] = t30;
    $[89] = t31;
  } else {
    t31 = $[89];
  }
  let t32;
  if ($[90] !== t29 || $[91] !== t31) {
    t32 = <NavigationMenu.Item className="Header__nav-action Header__mobile-menu-trigger flex lg:hidden items-center" key="mobile hamburger menu" value="mobile" asChild={true}><div>{t29}{t31}</div></NavigationMenu.Item>;
    $[90] = t29;
    $[91] = t31;
    $[92] = t32;
  } else {
    t32 = $[92];
  }
  let t33;
  if ($[93] !== t25 || $[94] !== t32) {
    t33 = <NavigationMenu.List className="Header__menu-list" asChild={true}><div className="Header__desktop-actions flex items-center gap-comfy-sm justify-end"><div className="flex flex-row gap-snug-md items-center md:gap-snug-xl lg:gap-comfy-sm">{t25}{t32}</div></div></NavigationMenu.List>;
    $[93] = t25;
    $[94] = t32;
    $[95] = t33;
  } else {
    t33 = $[95];
  }
  let t34;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <NavigationMenu.NavigationMenuIndicator />;
    $[96] = t34;
  } else {
    t34 = $[96];
  }
  let t35;
  if ($[97] !== handleViewportEnter || $[98] !== handleViewportLeave) {
    t35 = <div className="Header__viewport-position"><NavigationMenu.Viewport className="Header__menu-viewport flex justify-center" onPointerEnter={handleViewportEnter} onPointerLeave={handleViewportLeave} /></div>;
    $[97] = handleViewportEnter;
    $[98] = handleViewportLeave;
    $[99] = t35;
  } else {
    t35 = $[99];
  }
  let t36;
  if ($[100] !== handleActionNavValueChange || $[101] !== menuOpen || $[102] !== t24 || $[103] !== t33 || $[104] !== t35) {
    t36 = <NavigationMenu.Root key={t24} onValueChange={handleActionNavValueChange} value={menuOpen} className="Header__root flex flex-col" aria-label="Action navigation">{t33}{t34}{t35}</NavigationMenu.Root>;
    $[100] = handleActionNavValueChange;
    $[101] = menuOpen;
    $[102] = t24;
    $[103] = t33;
    $[104] = t35;
    $[105] = t36;
  } else {
    t36 = $[105];
  }
  let t37;
  if ($[106] !== t17 || $[107] !== t23 || $[108] !== t36) {
    t37 = <div className="Header__menu-list-container w-full">{t17}{t23}{t36}</div>;
    $[106] = t17;
    $[107] = t23;
    $[108] = t36;
    $[109] = t37;
  } else {
    t37 = $[109];
  }
  let t38;
  if ($[110] !== t13 || $[111] !== t37) {
    t38 = <div className={t13}>{t37}</div>;
    $[110] = t13;
    $[111] = t37;
    $[112] = t38;
  } else {
    t38 = $[112];
  }
  let t39;
  if ($[113] !== t10 || $[114] !== t12 || $[115] !== t38 || $[116] !== t9) {
    t39 = <header role="banner" className={t9}>{t10}{t12}{t38}</header>;
    $[113] = t10;
    $[114] = t12;
    $[115] = t38;
    $[116] = t9;
    $[117] = t39;
  } else {
    t39 = $[117];
  }
  return t39;
};
export default Header;
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(e_0) {
  return e_0.preventDefault();
}
function _temp3(e_1) {
  return e_1.preventDefault();
}